import { useGetShareQuery } from "@services/share";
import { useAppDispatch } from "hooks";
import { useState, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  addCoordinate,
  setApiError,
  setApiErrorType,
  setIsTracking,
  setShareId,
} from "store/slices";
import { Coordinate, ErrorType, ShareResponse } from "type";

export const useTokenAuth = () => {
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [shareData, setShareData] = useState<ShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  // Get shareId from URL params
  const shareId = searchParams.get("id");
  const location = useLocation();
  const isUnauthorized =
    location.pathname.includes("unauthorized") ||
    location.search.includes("unauthorized");
  // Real API call
  const {
    data: apiShareData,
    error: apiError,
    isLoading: apiLoading,
    isSuccess,
    isError,
  } = useGetShareQuery(shareId!, {
    skip: !shareId || isUnauthorized,
  });

  // Error type determination
  const determineErrorType = (
    data: ShareResponse | null,
    errorMessage?: string
  ): ErrorType => {
    if (!data && errorMessage?.includes("not found")) {
      return ErrorType.NOT_FOUND;
    }

    if (data) {
      const currentTimestamp = Date.now();

      if (currentTimestamp > data.endTimestamp) {
        return ErrorType.EXPIRED;
      }

      if (currentTimestamp < data.startTimestamp) {
        return ErrorType.FUTURE;
      }
    }

    if (errorMessage?.includes("network") || errorMessage?.includes("fetch")) {
      return ErrorType.NETWORK;
    }

    return ErrorType.UNKNOWN;
  };

  // Validation and processing logic
  const validateAndProcessShare = (data: ShareResponse): boolean => {
    try {
      setShareData(data);
      // Always dispatch share ID regardless of time validity
      dispatch(setShareId(data.shareId));
      // Get current timestamp
      const currentTimestamp = Date.now();

      // Check if current time is between start and end timestamps (inclusive)
      const isTimeValid =
        currentTimestamp >= data.startTimestamp &&
        currentTimestamp <= data.endTimestamp;

      setIsValid(isTimeValid);

      if (isTimeValid) {
        // Clear any previous errors
        setError(null);
        setErrorType(null);
        dispatch(setApiError(null));
        dispatch(setApiErrorType(null));

        // Dispatch share ID
        dispatch(setIsTracking(true));

        // Create coordinate object from location data
        const coordinate: Coordinate = {
          latitude: data.location.latitude.serviceValue,
          longitude: data.location.longitude.serviceValue,
          timestamp: new Date(
            data.location.latitude.time
          )?.toLocaleTimeString(), // Using latitude time as reference
        };

        // Dispatch coordinate
        dispatch(addCoordinate(coordinate));
      } else {
        // Set appropriate error type for invalid time
        const errorType = determineErrorType(data);
        setErrorType(errorType);
        dispatch(setApiErrorType(errorType));
        if (errorType === ErrorType.EXPIRED) {
          setError("Share link has expired");
        } else if (errorType === ErrorType.FUTURE) {
          setError("Share link is not yet active");
        }
      }

      return isTimeValid;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Error processing share data";
      setError(errorMessage);
      setErrorType(ErrorType.UNKNOWN);
      setIsValid(false);
      return false;
    }
  };

  // Main effect - Handle API response
  useEffect(() => {
    if (shareId) {
      setIsValidating(apiLoading);

      if (isSuccess && apiShareData) {
        setError(null);
        dispatch(setApiError(null));
        setErrorType(null);
        dispatch(setApiErrorType(null));
        validateAndProcessShare(apiShareData);
      }

      if (isError && apiError) {
        const err = apiError as {
          status: number;
          data: { message?: string } | string;
        };
        let errorType = ErrorType.UNKNOWN;
        let errorMessage =
          typeof err.data === "string"
            ? err.data
            : err.data?.message || "Something went wrong";
        setErrorType(errorType);
        setError(errorMessage);
        dispatch(setApiError(errorMessage));
        setIsValid(false);
        setIsValidating(false);
      }
    } else {
      // No share ID found in URL
      setIsValid(null);
      setIsValidating(false);
      setError(null);
      setErrorType(null);
      setShareData(null);
    }
  }, [shareId, apiShareData, isSuccess, isError, apiError, apiLoading]);

  // Public interface for manual validation
  const validateShareToken = async (
    targetShareId: string
  ): Promise<boolean> => {
    // For real API, validation is handled automatically by the useEffect
    if (shareData && shareData.shareId === targetShareId) {
      return validateAndProcessShare(shareData);
    }
    return false;
  };

  return {
    isValidating,
    isValid,
    shareData,
    error,
    errorType,
    validateShareToken,
  };
};
